/**
 * PageDetailLayout — compound page-layout voor entity-detail pages.
 *
 * Gebruik via subcomponents `.Header`, `.Tabs`, en `.Content`:
 *
 * ```tsx
 * <PageDetailLayout>
 *   <PageDetailLayout.Header>...</PageDetailLayout.Header>
 *   <PageDetailLayout.Tabs>...</PageDetailLayout.Tabs>
 *   <PageDetailLayout.Content>...</PageDetailLayout.Content>
 * </PageDetailLayout>
 * ```
 *
 * Slot subcomponents zijn first-class self-rendering: zij dragen hun
 * eigen wrapper-div met de juiste CSS-klasse, en kunnen daarom
 * ook standalone worden gebruikt (bv. binnen een framework-extension
 * override waar de outer PageDetailLayout niet beschikbaar is). De
 * compound `PageDetailLayout` doet alleen slot-discovery + placement
 * via `PageShell`.
 *
 * **Spacing-ownership:** de native BUI Header bepaalt zijn eigen inset;
 * de Header-slot voegt geen padding toe. De body/tabs hebben eigen spacing. De
 * `.Tabs`-slot neutraliseert de BackstageHeaderTabs paddingLeft via
 * descendant-selector.
 *
 * **Pattern reference:** zie `EntityLayout.Route` voor het
 * subcomponent-attachment-patroon binnen ns-theme.
 */

import type { ReactElement, ReactNode } from 'react';
import { Children, isValidElement } from 'react';

import { PageShell } from '../page-shell/page-shell';

import styles from './page-detail-layout.module.css';

/** @public */
export interface PageDetailLayoutProps {
  /**
   * Verwacht 1..3 children, één per slot (`Header`, `Tabs`, `Content`).
   * Onbekende children worden genegeerd.
   */
  children: ReactNode;
}

/** @public — props shared by every slot subcomponent. */
export interface PageDetailLayoutSlotProps {
  children?: ReactNode;
}

/**
 * @public — Content-slot inset variant.
 *
 * - `'l'` (default): 24px outer padding, matching the Tractie inset used
 *   by NS-controlled detail pages whose body has no framework-inner
 *   padding (e.g. TechRadarDetailPage).
 * - `'s'`: 12px outer padding, used when the body element brings its own
 *   12px inner padding (e.g. Backstage framework `EntityLayout` wrapped
 *   as a fallback for entity kinds without a dedicated NS page). 12 outer
 *   + 12 inner = 24 net so tabs and cards still align with the header.
 */
export type PageDetailLayoutContentInset = 's' | 'l';

/** @public — props for the `Content` slot. */
export interface PageDetailLayoutContentProps
  extends PageDetailLayoutSlotProps {
  inset?: PageDetailLayoutContentInset;
}

const HeaderSlot = (props: PageDetailLayoutSlotProps) => (
  <div className={styles.header}>{props.children}</div>
);
HeaderSlot.displayName = 'PageDetailLayout.Header';

const TabsSlot = (props: PageDetailLayoutSlotProps) => (
  <div className={styles.tabs}>{props.children}</div>
);
TabsSlot.displayName = 'PageDetailLayout.Tabs';

const ContentSlot = (props: PageDetailLayoutContentProps) => {
  const className =
    props.inset === 's' ? styles['content--inset-s'] : styles.content;
  return <div className={className}>{props.children}</div>;
};
ContentSlot.displayName = 'PageDetailLayout.Content';

function findSlotElement(
  children: ReactNode,
  slotComponent:
    | ((props: PageDetailLayoutSlotProps) => ReactElement)
    | ((props: PageDetailLayoutContentProps) => ReactElement),
): ReactElement | null {
  let found: ReactElement | null = null;
  Children.forEach(children, child => {
    if (isValidElement(child) && child.type === slotComponent) {
      found = child;
    }
  });
  return found;
}

/**
 * Best-effort name extraction for an unknown React child type. Falls back
 * to `'unknown'` when nothing identifiable is available. Used only for
 * dev-mode warning messages.
 */
function describeChildType(childType: unknown): string {
  if (typeof childType === 'string') {
    return childType;
  }
  if (typeof childType === 'function' || typeof childType === 'object') {
    if (childType !== null && 'displayName' in childType) {
      const displayName = Reflect.get(childType, 'displayName');
      if (typeof displayName === 'string' && displayName.length > 0) {
        return displayName;
      }
    }
    if (typeof childType === 'function' && childType.name.length > 0) {
      return childType.name;
    }
  }
  return 'unknown';
}

/**
 * Dev-only validation: warn when a child is not one of the known slots
 * (`Header` / `Tabs` / `Content`) or when a slot appears twice.
 * No-op in production builds.
 */
function validateChildren(children: ReactNode): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const knownSlots = new Set<unknown>([HeaderSlot, TabsSlot, ContentSlot]);
  const seen = new Set<unknown>();
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }
    const childType: unknown = child.type;
    if (!knownSlots.has(childType)) {
      console.warn(
        `[PageDetailLayout] Unknown child type: ${describeChildType(
          childType,
        )}. Expected Header / Tabs / Content.`,
      );
      return;
    }
    if (seen.has(childType)) {
      console.warn(
        `[PageDetailLayout] Duplicate slot child: ${describeChildType(
          childType,
        )}. Each slot should appear at most once.`,
      );
      return;
    }
    seen.add(childType);
  });
}

/** @public */
export const PageDetailLayout = (props: PageDetailLayoutProps) => {
  const { children } = props;

  validateChildren(children);

  const header = findSlotElement(children, HeaderSlot) ?? undefined;
  const subheader = findSlotElement(children, TabsSlot) ?? undefined;
  const content = findSlotElement(children, ContentSlot) ?? undefined;

  return (
    <PageShell header={header} subheader={subheader}>
      {content}
    </PageShell>
  );
};

PageDetailLayout.Header = HeaderSlot;
PageDetailLayout.Tabs = TabsSlot;
PageDetailLayout.Content = ContentSlot;
